import axios from "axios";
import { iamClient } from "@/api/request";

export const SAML_METADATA_MAX_BYTES = 100 * 1024;

export type SsoTestStatus = "passed" | "failed" | null;

export interface SsoConfig {
  configured: boolean;
  name: string;
  metadataFileName: string;
  metadataXml: string;
  loginUrl: string;
  spMetadataUrl: string;
  lastTestStatus: SsoTestStatus;
}

interface UserSsoConfigPayload {
  id: string;
  name: string;
  metadata: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

interface GetUserSsoConfigResponse {
  config: UserSsoConfigPayload | null;
  login_url: string;
  sp_metadata_url: string;
}

export interface SaveSsoConfigInput {
  name: string;
  enabled: boolean;
  metadata: string;
  testId?: string;
}

export interface SsoConnectionTest {
  testId: string;
  loginUrl: string;
}

export type SsoConnectionTestStatus = "pending" | "passed" | "failed" | "expired";

export interface SsoConnectionTestResult {
  testId: string;
  status: SsoConnectionTestStatus;
  message?: string;
}

interface TestUserSsoConfigResponse {
  test_id: string;
  login_url: string;
}

interface GetUserSsoTestResultResponse {
  test_id: string;
  status: SsoConnectionTestStatus;
  message?: string;
}

export function validateMetadataFile(file: File): string | null {
  if (!file.name.toLowerCase().endsWith(".xml")) {
    return "仅支持 XML 文件";
  }
  if (file.size > SAML_METADATA_MAX_BYTES) {
    return "XML 文件不能超过 100 KB";
  }
  return null;
}

function emptySsoConfig(): SsoConfig {
  return {
    configured: false,
    name: "",
    metadataFileName: "",
    metadataXml: "",
    loginUrl: "",
    spMetadataUrl: "",
    lastTestStatus: null,
  };
}

function normalizeUserSsoConfig(response: GetUserSsoConfigResponse): SsoConfig {
  const config = response.config;
  return {
    configured: config !== null,
    name: config?.name ?? "",
    metadataFileName: config?.metadata ? "idp-metadata.xml" : "",
    metadataXml: config?.metadata ?? "",
    loginUrl: response.login_url ?? "",
    spMetadataUrl: response.sp_metadata_url ?? "",
    lastTestStatus: config?.enabled ? "passed" : null,
  };
}

export const ssoApi = {
  getConfig: async (): Promise<SsoConfig> => {
    try {
      const response = await iamClient.post<GetUserSsoConfigResponse>("/v1/GetUserSSOConfig", {});
      return normalizeUserSsoConfig(response.data);
    } catch (error) {
      // 未创建配置是正常的首次使用状态。
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return emptySsoConfig();
      }
      throw error;
    }
  },

  testConnection: async (metadata: string): Promise<SsoConnectionTest> => {
    const response = await iamClient.post<TestUserSsoConfigResponse>("/v1/TestUserSSOConfig", {
      metadata,
    });
    return {
      testId: response.data.test_id,
      loginUrl: response.data.login_url,
    };
  },

  getTestResult: async (testId: string): Promise<SsoConnectionTestResult> => {
    const response = await iamClient.post<GetUserSsoTestResultResponse>(
      "/v1/GetUserSSOTestResult",
      { test_id: testId },
    );
    return {
      testId: response.data.test_id,
      status: response.data.status,
      ...(response.data.message ? { message: response.data.message } : {}),
    };
  },

  saveConfig: async (input: SaveSsoConfigInput): Promise<void> => {
    await iamClient.post("/v1/SetUserSSOConfig", {
      name: input.name.trim(),
      enabled: input.enabled,
      metadata: input.metadata,
      ...(input.testId ? { test_id: input.testId } : {}),
    });
  },
};
